import { EditFilled } from '@ant-design/icons';
import { useField } from '@framework/form/field/useField.tsx';
import { useController } from '@framework/form/hooks/useController.ts';
import { StaticInput } from '@framework/form/inputs/static/StaticInput.tsx';
import { ModalButton } from '@framework/ui/components/button/ModalButton.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { Space, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RichTextAdvancedRender } from '../../../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { ConfigFormItem } from '../../../../../../../ui/form/ConfigFormItem.tsx';
import { type IAsyncService } from '../../../../../../expressions/repository/async/context/IAsyncService.ts';
import { type IWidgetParsedProps } from '../../../common/util/getWidgetProps.ts';
import { type IAsyncSelectWidget } from '../../IAsyncSelectWidget.ts';
import { getSelectedValueLabel } from '../label/getSelectedValueLabel.ts';
import { useAsyncServiceTableColumns } from './useAsyncServiceTableColumns.tsx';

const FullWidthCompact = styled(Space.Compact)`
  width: 100%;
`;

export interface IAsyncServiceTableInputProps {
  widgetOptions: IWidgetParsedProps;
  data: any[] | null;
  isLoading: boolean;
  error: any;
  widget: IAsyncSelectWidget;
  objectFieldName: string;
  service: IAsyncService | null;
  setSearchQuery: (value: string | null) => void;
}

/**
 * Input async che permette la selezione attraverso una modal con una tabella.
 */
export function AsyncServiceTableInput(props: IAsyncServiceTableInputProps) {
  const {
    widgetOptions,
    data,
    isLoading,
    error,
    widget,
    objectFieldName,
    service,
  } = props;

  const field = useField(widgetOptions.name);
  const { field: objectField } = useController(objectFieldName);
  const value = useWatch({ name: widgetOptions.name });

  const labelValue = useMemo(() => {
    let values: Record<string, any> | null = null;
    if (widgetOptions.readonly) {
      // In sola lettura, prendo il valore direttamente dal campo
      values = objectField.value;
    } else {
      // In modalità di modifica, cerco il valore selezionato tra i dati
      values = data?.find(d => d[widget.valueField] === value);
    }
    if (!values) return '';
    return getSelectedValueLabel(widget, values);
  }, [data, objectField.value, value, widget, widgetOptions.readonly]);

  const dataSource = useMemo(() => data ?? [], [data]);

  const { t } = useTranslation();

  const columns = useAsyncServiceTableColumns(widget);

  const [selected, setSelected] = useState<Record<string, any> | null>(value);

  const handleSelect = useCallback(() => {
    field.setValue(selected?.[widget.valueField] ?? null, {
      // In questo modo resetta l'errore se presente (e.g. required)
      shouldValidate: true,
    });
    objectField.onChange(selected ?? null);
  }, [field, objectField, selected, widget.valueField]);

  return (
    // TODO Aggiungere pulsante per reset del valore
    <ConfigFormItem
      name={widgetOptions.name}
      label={<RichTextAdvancedRender inline text={widgetOptions.label} />}
      help={
        widgetOptions.helpText ? (
          <RichTextAdvancedRender inline text={widgetOptions.helpText} />
        ) : undefined
      }
      layout={{ hideVisualLabel: widgetOptions.hideLabel }}
      readonly={widgetOptions.readonly}
      widget={props.widget}
    >
      <FullWidthCompact>
        <StaticInput
          value={labelValue}
          placeholder={widgetOptions.placeholder}
          loading={isLoading}
        />
        {!widgetOptions.readonly && (
          <ModalButton
            buttonChildren={t('widgets.async-select.select-text', 'Scegli')}
            type="primary"
            size="large"
            modalProps={{
              okText: t('widgets.select-widget.select-text', 'Scegli'),
            }}
            onOk={handleSelect}
            style={{ width: '130px' }}
            icon={<EditFilled />}
          >
            <TableData
              dataSource={dataSource}
              size="small"
              loading={isLoading}
              pagination={false}
              columns={columns}
              darkTitleBar={false}
              rowKey={widget.valueField}
              rowSelection={{
                type: 'radio',
                onChange: (_, selectedRows) => {
                  setSelected(selectedRows[0]);
                },
                selectedRowKeys:
                  selected?.[widget.valueField] != null
                    ? [selected[widget.valueField]]
                    : undefined,
              }}
            />
          </ModalButton>
        )}
      </FullWidthCompact>
      {error && (
        <Typography.Text type="danger">
          {t(
            'widgets.async-select-widget-component.service-not-responding',
            'Impossibile recuperare i dati dal servizio {{service}}. Riprovare più tardi.',
            { service: service?.name ? `"${service.name}"` : '' },
          )}
        </Typography.Text>
      )}
    </ConfigFormItem>
  );
}
